import type { AxiosError } from "axios";
import { worktimeApi } from "@wkt/api/worktime";
import type { GenericServiceResponseDTO } from "@wkt/api/profileDTO";
import type {
  DocumentsInfoDTO,
  SignRequestValidationDTO,
} from "./documentsDTO";

type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;
type RequestOptions = {
  force?: boolean;
};
type UploadConfigDTO = {
  maxAttachmentBytes?: number;
};

const DOCUMENTS_INFO_CACHE_TTL_MS = 5000;
const DEFAULT_MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

export class DocumentsAPI {
  private documentsInfoCache: DocumentsInfoDTO | null = null;
  private documentsInfoCachedAt = 0;
  private documentsInfoRequest: Promise<DocumentsInfoDTO> | null = null;
  private maxAttachmentBytes = DEFAULT_MAX_ATTACHMENT_BYTES;
  private uploadConfigLoaded = false;
  private uploadConfigRequest: Promise<number> | null = null;

  getDocumentsInfo(
    onSuccess: SuccessCallback<DocumentsInfoDTO>,
    onError: ErrorCallback,
    options: RequestOptions = {},
  ): void {
    this.fetchDocumentsInfo(options)
      .then(onSuccess)
      .catch((error: AxiosError) => onError(error));
  }

  invalidateDocumentsInfo(): void {
    this.documentsInfoCache = null;
    this.documentsInfoCachedAt = 0;
  }

  getDocumentById(
    documentId: number,
    onSuccess: SuccessCallback<unknown>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericGet(`user/document/${documentId}`, onSuccess, onError);
  }

  requestSignCode(
    documentId: number,
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPut(
      `user/requestSignCode/${documentId}`,
      null,
      onSuccess,
      onError,
    );
  }

  validateSignCode(
    data: SignRequestValidationDTO,
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost("user/validateSignCode", data, onSuccess, onError);
  }

  uploadDocument(
    file: File,
    itemId: number,
    onSuccess: SuccessCallback<string>,
    onError: ErrorCallback,
  ): void {
    // WKT-2322: migrado de base64 a multipart
    const formData = new FormData();
    formData.append("file", file);
    formData.append("encoded-filename", file.name);
    formData.append("item-id", String(itemId));

    worktimeApi.genericPost<string>(
      "user/uploadDocument",
      formData,
      onSuccess,
      onError,
    );
  }

  getMaxAttachmentBytes(): Promise<number> {
    if (this.uploadConfigLoaded) {
      return Promise.resolve(this.maxAttachmentBytes);
    }

    if (this.uploadConfigRequest) {
      return this.uploadConfigRequest;
    }

    this.uploadConfigRequest = new Promise((resolve) => {
      worktimeApi.genericGet<UploadConfigDTO>(
        "upload-config",
        (response) => {
          const configuredValue = Number(response?.maxAttachmentBytes);
          if (Number.isFinite(configuredValue) && configuredValue > 0) {
            this.maxAttachmentBytes = configuredValue;
          }
          this.uploadConfigLoaded = true;
          this.uploadConfigRequest = null;
          resolve(this.maxAttachmentBytes);
        },
        () => {
          this.uploadConfigLoaded = true;
          this.uploadConfigRequest = null;
          resolve(this.maxAttachmentBytes);
        },
      );
    });

    return this.uploadConfigRequest;
  }

  uploadSignaturePicture(
    documentId: number,
    dataUrl: string,
    onSuccess: SuccessCallback<unknown>,
    onError: ErrorCallback,
  ): void {
    worktimeApi.genericPost(
      "user/uploadSignaturePicture",
      `${documentId}&${dataUrl}`,
      onSuccess,
      onError,
    );
  }

  private fetchDocumentsInfo(
    options: RequestOptions = {},
  ): Promise<DocumentsInfoDTO> {
    const forceRefresh = options.force === true;
    const now = Date.now();

    if (
      !forceRefresh &&
      this.documentsInfoCache &&
      now - this.documentsInfoCachedAt < DOCUMENTS_INFO_CACHE_TTL_MS
    ) {
      return Promise.resolve(this.documentsInfoCache);
    }

    if (!forceRefresh && this.documentsInfoRequest) {
      return this.documentsInfoRequest;
    }

    this.documentsInfoRequest = new Promise((resolve, reject) => {
      worktimeApi.genericGet(
        "user/documents-info",
        (response) => {
          this.documentsInfoCache = response ?? null;
          this.documentsInfoCachedAt = Date.now();
          this.documentsInfoRequest = null;
          resolve(response);
        },
        (error) => {
          this.documentsInfoRequest = null;
          reject(error);
        },
      );
    });

    return this.documentsInfoRequest;
  }
}

export const documentsApi = new DocumentsAPI();
export default documentsApi;
